<template>
  <a
    :href="href"
    :class="['zg-tile', {
      'zg-tile--product': product && !compact,
      'zg-tile--compact': product && compact,
      'zg-tile--full-width': fullWidth,
      'zg-tile--full-width-mobile': fullWidthMobile,
      'zg-tile--disabled': disabled,
      [`zg-tile--background-${background}`]: background,
    }]"
    type="button"
    @click="$emit('click')"
  >
    <zg-label
      v-if="tag"
      :text="tag"
      class="tag"
      size="small"
    />

    <span
      v-if="product"
      class="product"
    >
      <zg-product-icon
        :product="product"
        class="icon"
      />
    </span>

    <span class="text">
      <span>{{ text }}</span>

      <zg-icon
        icon="chevron"
        rotate="270"
        size="small"
      />
    </span>
  </a>
</template>

<script>
  import ZgIcon from '../ZgIcon/ZgIcon'
  import ZgLabel from '../ZgLabel/ZgLabel'
  import ZgProductIcon from '../ZgProductIcon/ZgProductIcon'

  export default {
    name: 'ZgTile',
    components: {
      ZgIcon,
      ZgLabel,
      ZgProductIcon
    },
    props: {
      product: {
        type: String,
        required: false,
        default: null
      },
      href: {
        type: String,
        required: true
      },
      text: {
        type: String,
        required: true
      },
      tag: {
        type: String,
        required: false,
        default: null
      },
      background: {
        type: String,
        required: false,
        default: 'beige',
        validator: value => [
          'beige',
          'white'
        ].includes(value)
      },
      fullWidth: {
        type: Boolean,
        required: false,
        default: false
      },
      fullWidthMobile: {
        type: Boolean,
        required: false,
        default: false
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      compact: {
        type: Boolean,
        required: false,
        default: false
      }
    }
  }
</script>

<style lang="scss" scoped>
  $border-width: rem(4px);

  .zg-tile {
    position: relative;
    display: inline-flex;
    justify-content: flex-start;
    align-items: center;
    min-width: rem(165px);
    height: rem(60px);
    padding: rem(16px) rem(24px);
    border-radius: rem(99px);
    border: none;
    outline: none;
    transition: all 0.3s ease;
    cursor: pointer;

    &:hover {
      color: color(brand-500);

      .text svg {
        color: color(brand-500);
      }
    }

    &:focus {
      box-shadow: box-shadow(focus);
    }

    &:focus:not(:focus-visible) {
      box-shadow: none;
    }

    .tag {
      position: absolute;
      top: 0;
      right: rhythm(xsmall);
      transform: translateY(-50%);
    }

    .icon {
      width: rem(48px);
      height: rem(48px);

      @include mq(medium) {
        width: rem(88px);
        height: rem(88px);
      }

      svg {
        color: color(brand-500);
      }
    }

    .text {
      display: inline-flex;
      align-items: center;
      justify-content: space-between;
      flex-grow: 1;
      gap: rem(6px);

      span {
        @include type-title-s;

        margin: 0;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // background
  // ? ----------------------------------------------------------------------

  .zg-tile--background-beige {
    background-color: color(beige);
  }

  .zg-tile--background-white {
    background-color: color(white);
  }

  // ? ----------------------------------------------------------------------
  // product
  // ? ----------------------------------------------------------------------

  .zg-tile--product {
    height: rem(64px);
    gap: rem(16px);
    background-color: color(brand-100);
    border: $border-width solid color(brand-100);

    @include mq(medium) {
      flex-direction: column;
      height: auto;
      gap: 0;
      min-width: auto;
      background-color: transparent;
      border: none;
      padding: 0;
      margin: 0;
    }

    .product {
      @include mq(medium) {
        display: flex;
        align-items: center;
        justify-content: center;
        width: rem(160px);
        height: rem(160px);
        background-color: color(brand-100);
        border: $border-width solid color(brand-100);
        margin: 0 0 rhythm(xsmall);
        border-radius: 100%;
        transition: all 0.3s ease;
      }
    }

    &:hover {
      color: color(black);

      .product {
        @include mq(medium) {
          background-color: color(brand-300);
          border-color: color(brand-300);
        }
      }

      .text svg {
        color: color(black);
      }
    }

    &:focus {
      border-color: color(brand-300);

      @include mq(medium) {
        box-shadow: none;
      }

      .product {
        @include mq(medium) {
          background-color: color(brand-100);
          border-color: color(brand-300);
          box-shadow: box-shadow(focus);
        }
      }
    }

    &:focus:not(:focus-visible) {
      border-color: color(brand-100);

      @include mq(medium) {
        box-shadow: none;
      }

      .product {
        @include mq(medium) {
          background-color: color(brand-100);
          border-color: color(brand-100);
          box-shadow: none;
        }
      }
    }

    .tag {
      top: -$border-width;

      @include mq(medium) {
        right: -$border-width;
        transform: none;
      }
    }
  }

  // ? ----------------------------------------------------------------------
  // compact
  // ? ----------------------------------------------------------------------
  .zg-tile--compact {
    height: rem(64px);
    background-color: color(brand-100);
    border: $border-width solid color(brand-100);
    display: flex;
    justify-content: space-between;
    text-align: left;

    .product {
      @include mq(medium) {
        display: flex;
        justify-content: space-between;
        align-items: center;
        text-align: left;
        flex: 1 0 0;
        border: unset;
        max-width: rem(64px);
      }
    }

    .icon {
      width: 3rem;
      height: 3rem;
      max-width: 3rem;
      margin-right: 1rem;
    }
  }

  // ? ----------------------------------------------------------------------
  // modifiers
  // ? ----------------------------------------------------------------------

  .zg-tile--full-width {
    width: 100%;
    min-width: 100%;

    @include mq(medium) {
      width: 100%;
      min-width: 100%;
    }
  }

  .zg-tile--full-width-mobile {
    @include mq($until: medium) {
      width: 100%;
      min-width: 100%;
    }
  }

  .zg-tile--disabled {
    @include disabled;
  }
</style>
